/**
 * Generic form that renders config inputs for any activity based on its descriptor.
 * Supports: difficulty, select (mode), multiselect (ops), theme.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { themes as themesApi, type ThemeSummary } from '../api/client'
import type { ActivityConfigDescriptor, PlaybookItemConfig } from './activityConfig'

interface Props {
  descriptor: ActivityConfigDescriptor
  value:      PlaybookItemConfig
  onChange:   (next: PlaybookItemConfig) => void
}

export default function ActivityConfigForm({ descriptor, value, onChange }: Props) {
  const { t, language } = useTranslation()
  const p = t.professional
  const [availableThemes, setAvailableThemes] = useState<ThemeSummary[]>([])

  const hasThemeField = descriptor.fields.some(f => f.type === 'theme')

  useEffect(() => {
    if (!hasThemeField) return
    themesApi.list(descriptor.slug).then(setAvailableThemes).catch(() => {})
  }, [hasThemeField, descriptor.slug])

  const set = (key: string, val: unknown) => {
    onChange({ ...value, [key]: val })
  }

  return (
    <div style={s.root}>
      {descriptor.fields.map((field, i) => {
        if (field.type === 'difficulty') {
          const opts = [
            { id: 'easy',   label: p.difficultyEasy },
            { id: 'medium', label: p.difficultyMedium },
            { id: 'hard',   label: p.difficultyHard },
          ]
          return (
            <div key={i} style={s.section}>
              <div style={s.label}>{p.difficulty}</div>
              <div style={s.btnRow}>
                {opts.map(o => (
                  <button
                    key={o.id}
                    style={{ ...s.optBtn, ...(value.difficulty === o.id ? s.optBtnActive : {}) }}
                    onClick={() => set('difficulty', o.id)}
                    type="button"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )
        }

        if (field.type === 'select') {
          const current = value[field.key as keyof PlaybookItemConfig] as string | undefined
          const fieldLabel = language === 'es' ? field.labelEs : field.labelEn
          return (
            <div key={i} style={s.section}>
              <div style={s.label}>{fieldLabel}</div>
              <div style={s.btnRow}>
                {field.options.map(o => (
                  <button
                    key={o.value}
                    style={{ ...s.optBtn, ...(current === o.value ? s.optBtnActive : {}) }}
                    onClick={() => set(field.key, o.value)}
                    type="button"
                  >
                    {language === 'es' ? o.labelEs : o.labelEn}
                  </button>
                ))}
              </div>
            </div>
          )
        }

        if (field.type === 'multiselect') {
          const current = (value[field.key as keyof PlaybookItemConfig] as string[] | undefined) ?? []
          const fieldLabel = language === 'es' ? field.labelEs : field.labelEn
          const toggle = (v: string) => {
            const next = current.includes(v)
              ? current.filter(x => x !== v)
              : [...current, v]
            if (next.length < field.minSelected) return
            set(field.key, next)
          }
          return (
            <div key={i} style={s.section}>
              <div style={s.label}>{fieldLabel}</div>
              <div style={s.btnRow}>
                {field.options.map(o => (
                  <button
                    key={o.value}
                    style={{ ...s.optBtn, ...(current.includes(o.value) ? s.optBtnActive : {}) }}
                    onClick={() => toggle(o.value)}
                    type="button"
                    aria-pressed={current.includes(o.value)}
                  >
                    {language === 'es' ? o.labelEs : o.labelEn}
                  </button>
                ))}
              </div>
            </div>
          )
        }

        if (field.type === 'theme') {
          const current = value.theme
          const mode = value.mode as string | undefined
          const filtered = availableThemes
          return (
            <div key={i} style={s.section}>
              <div style={s.label}>{p.theme}</div>
              <div style={s.themeRow}>
                {/* Classic / no theme */}
                <button
                  style={{ ...s.themeBtn, ...(!current ? s.themeBtnActive : {}) }}
                  onClick={() => set('theme', null)}
                  type="button"
                >
                  <span style={s.themeEmoji}>🃏</span>
                  <span style={s.themeLabel}>{p.themeClassic}</span>
                </button>
                {filtered.map(th => {
                  // For wordsearch, skip themes that have too few words for the chosen difficulty
                  if (descriptor.slug === 'wordsearch' && mode) {
                    const diff = (value.difficulty ?? 'easy') as 'easy' | 'medium' | 'hard'
                    const count = th.wordsearchFit?.[language as 'es' | 'en']?.[diff] ?? 0
                    if (count < 4) return null
                  }
                  const isActive = current?.slug === th.slug
                  return (
                    <button
                      key={th.slug}
                      style={{ ...s.themeBtn, ...(isActive ? s.themeBtnActive : {}) }}
                      onClick={() => set('theme', { id: th.id, slug: th.slug })}
                      type="button"
                    >
                      {th.coverUrl
                        ? <img src={th.coverUrl} alt="" style={s.themeCover} />
                        : <span style={s.themeEmoji}>🖼️</span>
                      }
                      <span style={s.themeLabel}>{language === 'es' ? th.nameEs : th.nameEn}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        }

        return null
      })}
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '16px',
  },
  section: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '8px',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:      '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  btnRow: {
    display:  'flex',
    flexWrap: 'wrap',
    gap:      '8px',
  },
  optBtn: {
    padding:      '8px 16px',
    minHeight:    '48px',
    borderRadius: '10px',
    border:       '2px solid #ddd',
    background:   '#fff',
    fontSize: '0.9375rem',
    cursor:       'pointer',
    color:        '#444',
    fontWeight:   500,
  },
  optBtnActive: {
    border:     '2px solid #5B8A5F',
    background: '#EAF3EB',
    color:      '#2D5A32',
    fontWeight: 700,
  },
  themeRow: {
    display:  'flex',
    flexWrap: 'wrap',
    gap:      '8px',
  },
  themeBtn: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            '4px',
    padding:        '8px',
    minWidth:       '72px',
    minHeight:      '80px',
    borderRadius:   '12px',
    border:         '2px solid #ddd',
    background:     '#fff',
    cursor:         'pointer',
  },
  themeBtnActive: {
    border:     '2px solid #5B8A5F',
    background: '#EAF3EB',
  },
  themeEmoji: {
    fontSize: '1.75rem',
  },
  themeCover: {
    width:        '48px',
    height:       '48px',
    borderRadius: '8px',
    objectFit:    'cover',
  },
  themeLabel: {
    fontSize: '0.875rem',
    color:     '#555',
    textAlign: 'center',
    maxWidth:  '70px',
    overflow:  'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}
